import {
  FeatureFlags,
  Integration,
  INTEGRATION_DISPLAY_NAMES,
  isMergeIntegration,
  isSupportedIntegration
} from '@kindo/universal';
import { useFlag } from '@unleash/nextjs';
import React from 'react';

import { Size, Table, Toggle, ToolTip } from '~/components/core';
import { useCanAccessSecuritySettings } from '~/hooks';

type IntegrationAccessTableProps = {
  handleIntegrationToggle: (
    integration: Integration,
    isChecked: boolean
  ) => void;
  integrationConfigs: {
    accessEnabled: boolean;
    integration: Integration;
  }[];
};

const IntegrationAccessTable: React.FC<IntegrationAccessTableProps> = ({
  integrationConfigs,
  handleIntegrationToggle
}) => {
  const { userCanAccessSecuritySettings } = useCanAccessSecuritySettings();
  const canUseMerge = useFlag(FeatureFlags.MERGE_INTEGRATION);
  // If MERGE_INTEGRATION feature flag is disabled, then filter out non-Merge integrations
  const filteredOrgIntegrationSecurityConfigs = canUseMerge
    ? integrationConfigs
    : integrationConfigs.filter(
        (config) => !isMergeIntegration(config.integration)
      );
  // sort orgIntegrationSecurityConfig so that supported integrations are at the top
  const sortedOrgIntegrationSecurityConfigs =
    filteredOrgIntegrationSecurityConfigs.sort(
      (a, b) =>
        Number(isSupportedIntegration(b.integration)) -
        Number(isSupportedIntegration(a.integration))
    );
  return (
    <Table
      columns={[
        {
          title: 'Name'
        },
        {
          title: 'Status',
          width: 'w-1/6'
        }
      ]}
      noRowsText="No integration settings available at this time."
      rows={sortedOrgIntegrationSecurityConfigs.map(
        ({ accessEnabled, integration }) => {
          const isSupported = isSupportedIntegration(integration);
          return {
            key: integration,
            cells: [
              INTEGRATION_DISPLAY_NAMES[integration],
              <ToolTip
                content="Contact sales to enable this integration"
                disabled={isSupported}
                key={`user-group-integration-access-toggle-${integration}`}
              >
                <span className="w-fit" data-testid="tooltip-span-wrapper">
                  <Toggle
                    checked={accessEnabled && isSupported}
                    disabled={!userCanAccessSecuritySettings || !isSupported}
                    onToggle={(isChecked: boolean) =>
                      handleIntegrationToggle(integration, isChecked)
                    }
                    size={Size.SMALL}
                  />
                </span>
              </ToolTip>
            ]
          };
        }
      )}
    />
  );
};

export default IntegrationAccessTable;
